import React, { useCallback } from 'react';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import RecordCardList from '../RecordCards/RecordCardList';
import { ListMembershipChips } from './RegistrosLotCard';
import { RECORD_KIND } from '../../utils/recordCards';
import { getLotMatchingListLabels, getUnitMatchingListLabels } from '../../utils/userLotUnits';
import { canLocateCadParcelOnCountryMap } from '../../../Showroom/utils/cadRegistros';

const iconSx = { fontSize: 20 };

/**
 * Vista «por registro» de REGISTROS: una card por lote, UF o lote CAD, con el
 * mismo ⋮ que la vista agrupada (Ubicar, Editar, Ver lote, Compartir) según el
 * tipo. Las acciones reciben las filas originales (`item.lot` / `item.unit` /
 * `item.parcel`).
 */
export default function RegistrosRecordCards({
  items,
  layout,
  isRecordLocated,
  onLocateLot,
  onLocateCadParcel,
  onOpenLot,
  onOpenUnit,
  onOpenCadParcel,
  onShareLot,
  showListMembership = false,
  listFilters = [],
  userLists = [],
  noListsValue,
}) {
  const kindOf = (item) => item.record?.record_kind;

  const handleOpen = useCallback((item) => {
    const kind = item.record?.record_kind;
    if (kind === RECORD_KIND.CAD) {
      onOpenCadParcel?.(item.parcel);
      return;
    }
    if (kind === RECORD_KIND.UNIT) {
      onOpenUnit?.(item.lot, item.unit);
      return;
    }
    onOpenLot?.(item.lotId, { openLotContent: item.hasLotContent });
  }, [onOpenCadParcel, onOpenUnit, onOpenLot]);

  const getActions = (item) => {
    const kind = kindOf(item);
    const located = isRecordLocated?.(item);
    if (kind === RECORD_KIND.CAD) {
      const canLocate = canLocateCadParcelOnCountryMap(item.parcel);
      return [
        {
          key: 'locate',
          label: 'Ubicar en el mapa',
          icon: <MyLocationOutlinedIcon sx={iconSx} />,
          disabled: !canLocate,
          onClick: () => { if (canLocate) onLocateCadParcel?.(item.parcel); },
        },
        { key: 'open', label: 'Ver lote', icon: <VisibilityOutlinedIcon sx={iconSx} />, onClick: handleOpen },
      ];
    }
    const actions = [{
      key: 'locate',
      label: located ? 'Quitar marca del mapa' : 'Ubicar en el mapa',
      icon: <MyLocationOutlinedIcon sx={iconSx} />,
      onClick: () => onLocateLot?.(item.lotId, item.lot, item.key),
    }];
    if (kind === RECORD_KIND.UNIT) {
      actions.push({ key: 'edit', label: 'Editar', icon: <EditIcon sx={iconSx} />, onClick: handleOpen });
      actions.push({
        key: 'lot',
        label: 'Ver lote',
        icon: <VisibilityOutlinedIcon sx={iconSx} />,
        onClick: () => onOpenLot?.(item.lotId),
      });
    } else {
      actions.push({
        key: 'open',
        label: item.hasLotContent ? 'Ver contenido del lote' : 'Ver lote',
        icon: <VisibilityOutlinedIcon sx={iconSx} />,
        onClick: handleOpen,
      });
    }
    if (onShareLot) {
      actions.push({
        key: 'share',
        label: 'Compartir',
        icon: <ShareIcon sx={iconSx} />,
        onClick: () => onShareLot(item.lot),
      });
    }
    return actions;
  };

  const getFooterExtra = showListMembership
    ? (item) => {
      const kind = kindOf(item);
      if (kind === RECORD_KIND.CAD) return null;
      const labels = kind === RECORD_KIND.UNIT
        ? getUnitMatchingListLabels(item.unit, listFilters, userLists, noListsValue)
        : getLotMatchingListLabels(item.lot, listFilters, userLists, noListsValue);
      return labels.length ? <ListMembershipChips labels={labels} singleLine /> : null;
    }
    : undefined;

  return (
    <RecordCardList
      items={items}
      layout={layout}
      isItemLocated={isRecordLocated}
      getActions={getActions}
      getFooterExtra={getFooterExtra}
      onOpen={handleOpen}
    />
  );
}
